/**
 * Экран «Клиенты» консультанта (UC-3) — вход во всю консультантскую ветку.
 *
 * Дерево отделов — навигация, таблица — работа. Клиенты приезжают из внешней
 * системы продаж; руками их здесь не заводят.
 *
 * **Страница, а не весь пул (AK-627).** Раньше экран тянул все 11 244 записи
 * одним ответом (~6,4 МБ), рисовал их одной таблицей и фильтровал в браузере
 * на каждое нажатие клавиши — отсюда «медленно грузится, с тормозами работает
 * поиск». Теперь отбор, поиск и нарезка живут на сервере, а фронт рисует ровно
 * одну страницу. Правило проекта «полный список по алфавиту + поиск» при этом
 * не нарушено: порядок сквозной алфавитный, никаких «часто используемых»
 * сверху, поиск идёт по ВСЕМ отделам.
 *
 * Состояние экрана (страница, запрос, отдел, фильтр) живёт в URL — ссылку на
 * найденного клиента можно отправить коллеге, а «назад» возвращает туда, где
 * человек был, а не в начало списка.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { consultantClientsRoute, router } from '@/router';
import { ApiError } from '@/lib/api';
import { useIsMobile } from '@/lib/useBreakpoint';
import { listConsultantClients } from '@/lib/consultant/api';
import { useClientFlow } from '@/lib/consultant/useClientFlow';
import { ClientActions } from '@/components/consultant/ClientActions';
import {
  FarmSetupDialog,
  type DepartmentOption,
} from '@/components/consultant/FarmSetupDialog';
import { api } from '@/lib/api';
import { pageRangeLabel, syncAgeLabel, topLevelIds } from '@/lib/consultant/format';
import type {
  ClientTreeNode,
  ConsultantClient,
  ConsultantClientsResponse,
} from '@/lib/consultant/types';

/** Узел «все клиенты» — псевдо-корень дерева (в URL не попадает). */
const ROOT = '__all__';
/** Узел для клиентов, чей ответственный не найден в структуре отделов. */
const UNASSIGNED = '__unassigned__';
/** Размер страницы. Столько человек просматривает; остальное он листает. */
const PER_PAGE = 50;
/**
 * Пауза перед походом на сервер. 300 мс — обычная скорость набора: запрос
 * уходит один раз на слово, а не по разу на букву.
 */
const SEARCH_DEBOUNCE_MS = 300;

// ─────────────────────────── дерево ───────────────────────────

function TreeBranch({
  nodes,
  selected,
  expanded,
  onSelect,
  onToggle,
  depth = 0,
}: {
  nodes: ClientTreeNode[];
  selected: string;
  expanded: Set<string>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  depth?: number;
}) {
  return (
    <>
      {nodes.map((node) => {
        const isOpen = expanded.has(node.id);
        const hasKids = node.children.length > 0;
        return (
          <div key={node.id}>
            <div
              className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm ${
                selected === node.id ? 'bg-accent-brand/15 font-medium' : 'hover:bg-accent'
              }`}
              style={{ paddingLeft: `${8 + depth * 14}px` }}
            >
              {hasKids ? (
                <button
                  type="button"
                  aria-label={isOpen ? 'Свернуть' : 'Развернуть'}
                  onClick={() => onToggle(node.id)}
                  className="shrink-0 text-muted-foreground"
                >
                  {isOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              <button
                type="button"
                onClick={() => onSelect(node.id)}
                className="min-w-0 flex-1 truncate text-left"
              >
                {node.title}
              </button>
              <span className="shrink-0 text-xs text-muted-foreground">{node.count}</span>
            </div>
            {hasKids && isOpen && (
              <TreeBranch
                nodes={node.children}
                selected={selected}
                expanded={expanded}
                onSelect={onSelect}
                onToggle={onToggle}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ─────────────────────────── строка состояния ───────────────────────────

/**
 * Состояние клиента одним бейджем — три значения из утверждённого флоу.
 *
 * Бейдж больше НЕ носит в себе кнопку: действия живут отдельной колонкой и
 * одинаковы для всех строк (шаг 4). Кнопка внутри состояния означала, что у
 * разных строк разный набор управляющих элементов в одном и том же месте —
 * ровно то, из-за чего каждую строку приходилось перечитывать.
 */
function StateCell({ client }: { client: ConsultantClient }) {
  if (client.state === 'in_system') {
    return (
      <Badge variant="primary" data-testid={`fields-count-${client.external_id}`}>
        полей: {client.fields_count}
      </Badge>
    );
  }
  if (client.state === 'farm_ready') {
    return (
      <Badge variant="accent-brand" data-testid={`state-farm-ready-${client.external_id}`}>
        заведён · полей нет
      </Badge>
    );
  }
  return (
    <Badge variant="outline" data-testid={`state-absent-${client.external_id}`}>
      нет в системе
    </Badge>
  );
}

// ─────────────────────────── страница ───────────────────────────

export function ConsultantClientsPage() {
  const isMobile = useIsMobile();
  const search = consultantClientsRoute.useSearch();
  const page = search.page ?? 1;
  const query = search.q ?? '';
  const selected = search.node ?? ROOT;
  const onlyInSystem = search.inSystem === 1;

  const [data, setData] = useState<ConsultantClientsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState(query);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  //ptr Отделы тянем один раз на экран: их единицы, а модалка открывается часто.
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  /** Номер последнего отправленного запроса — против гонки ответов. */
  const reqId = useRef(0);

  /**
   * Правка адреса — единственный способ сменить состояние экрана: страница,
   * запрос и отдел приходят ОТТУДА. Иначе URL и таблица разъезжаются, и
   * присланная коллеге ссылка открывает не то, что было на экране.
   */
  const patchSearch = useCallback(
    (patch: Record<string, unknown>) => {
      void router.navigate({
        to: '/clients',
        search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
        replace: true,
      });
    },
    [],
  );

  const fetchPage = useCallback(
    async (refresh = false) => {
      const id = ++reqId.current;
      if (refresh) setRefreshing(true);
      setError(null);
      try {
        const resp = await listConsultantClients({
          page,
          perPage: PER_PAGE,
          q: query,
          node: selected === ROOT ? undefined : selected,
          onlyInSystem,
          refresh,
        });
        // Ответ на устаревший запрос игнорируем: при быстром наборе ответы
        // приходят не в том порядке, и последним на экране оказывается не
        // тот, что человек набрал последним.
        if (id !== reqId.current) return;
        setData(resp);
        setExpanded((prev) => (prev.size ? prev : new Set(topLevelIds(resp.tree.nodes))));
      } catch (e) {
        if (id !== reqId.current) return;
        setError(
          e instanceof ApiError
            ? e.detail
            : 'Список клиентов временно недоступен. Попробуйте обновить через несколько минут.',
        );
      } finally {
        if (id === reqId.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [page, query, selected, onlyInSystem],
  );

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  // Отделы — один запрос на экран. Их единицы, а модалка «Завести хозяйство»
  // открывается часто: тянуть список на каждое открытие значит держать человека
  // перед пустой выпадашкой ради данных, которые не меняются неделями.
  useEffect(() => {
    let cancelled = false;
    api
      .get<DepartmentOption[]>('/api/v1/clusters')
      .then((rows) => {
        if (!cancelled && Array.isArray(rows)) setDepartments(rows);
      })
      .catch(() => {
        // Без отделов заводить хозяйство можно — просто без отдела.
        if (!cancelled) setDepartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Поиск с паузой: запрос уходит, когда человек перестал печатать. Без
  // паузы это запрос на КАЖДУЮ букву, с ней — один на слово.
  useEffect(() => {
    if (input === query) return;
    const t = setTimeout(
      () => patchSearch({ q: input.trim() || undefined, page: undefined }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
  }, [input, query, patchSearch]);

  // Адрес поменялся мимо поля ввода («назад», присланная ссылка) — строка
  // поиска обязана это показать, иначе она врёт про то, что найдено.
  useEffect(() => setInput(query), [query]);

  const rows = data?.items ?? [];
  const pages = data?.pages ?? 1;
  const total = data?.total ?? 0;

  const selectNode = (id: string) =>
    patchSearch({ node: id === ROOT ? undefined : id, page: undefined });

  const goto = (next: number) =>
    patchSearch({ page: next <= 1 ? undefined : next });

  // «Завести хозяйство» — модалка одна на весь список: клиент в ней меняется,
  // а не заводится по модалке на строку.
  const [setupClient, setSetupClient] = useState<ConsultantClient | null>(null);
  const flow = useClientFlow(() => void fetchPage());

  const handleFarmSetup = async (result: Parameters<typeof flow.applyFarmSetup>[1]) => {
    if (!setupClient) return;
    setBusyId(setupClient.external_id);
    const ok = await flow.applyFarmSetup(
      { external_id: setupClient.external_id, crm_company_id: setupClient.crm_company_id },
      result,
    );
    setBusyId(null);
    if (ok && !result.addFields) setSetupClient(null);
  };

  const runAction = async (
    client: ConsultantClient,
    action: (c: { external_id: string; crm_company_id: string | null }) => Promise<void>,
  ) => {
    setBusyId(client.external_id);
    await action({ external_id: client.external_id, crm_company_id: client.crm_company_id });
    setBusyId(null);
  };

  // Строка ведёт в карточку клиента (AK-979) — у ЛЮБОГО клиента списка, а не
  // только у заведённого: карточка собирается из CRM и бота, своя запись CRM
  // для неё не нужна.
  const openClient = (client: ConsultantClient) => {
    void router.navigate({
      to: '/clients/$externalId',
      params: { externalId: client.external_id },
    });
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rangeLabel = useMemo(
    () => pageRangeLabel(data?.page ?? page, data?.per_page ?? PER_PAGE, total),
    [data, page, total],
  );

  if (loading && !data) {
    return (
      <AppShell>
        <div className="space-y-2" data-testid="consultant-clients-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  const tree = data?.tree.nodes ?? [];

  return (
    <AppShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">Клиенты</h1>
          <div className="flex items-center gap-2">
            <span
              className={`text-sm ${data?.stale ? 'text-destructive' : 'text-muted-foreground'}`}
              data-testid="sync-age"
            >
              {syncAgeLabel(data?.synced_at ?? null)}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void fetchPage(true)}
              disabled={refreshing}
              data-testid="refresh-clients"
            >
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Обновить сейчас
            </Button>
          </div>
        </div>

        {data?.stale && (
          <Alert variant="destructive" data-testid="stale-warning">
            <AlertDescription>
              Данные из внешней системы устарели. Новые клиенты и смена
              ответственных могут не отображаться — работать при этом можно.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" data-testid="clients-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className={isMobile ? 'space-y-3' : 'grid grid-cols-[240px_1fr] gap-4'}>
          {/* Дерево держит структуру продаж перед глазами — важно руководителю
              отдела. На узком экране оно съедает половину ширины, поэтому там
              его заменяет выпадающий выбор отдела (Р5: А для компьютера). */}
          <Card className="p-2">
            <div className="mb-2 flex items-center gap-2 rounded-md border px-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Название или ИНН"
                className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
                data-testid="clients-search"
              />
            </div>
            {!isMobile && (
              <div className="max-h-[60vh] overflow-y-auto">
                <div
                  className={`flex items-center justify-between rounded-md px-2 py-1 text-sm ${
                    selected === ROOT ? 'bg-accent-brand/15 font-medium' : 'hover:bg-accent'
                  }`}
                >
                  <button type="button" onClick={() => selectNode(ROOT)} className="flex-1 text-left">
                    Все клиенты
                  </button>
                  <span className="text-xs text-muted-foreground">{data?.pool_total ?? 0}</span>
                </div>
                <TreeBranch
                  nodes={tree}
                  selected={selected}
                  expanded={expanded}
                  onSelect={selectNode}
                  onToggle={toggle}
                />
                {(data?.tree.unassigned ?? 0) > 0 && (
                  <div
                    className={`flex items-center justify-between rounded-md px-2 py-1 text-sm ${
                      selected === UNASSIGNED ? 'bg-accent-brand/15 font-medium' : 'hover:bg-accent'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => selectNode(UNASSIGNED)}
                      className="flex-1 text-left"
                    >
                      Без отдела
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {data?.tree.unassigned}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 text-sm text-muted-foreground">
              <span data-testid="clients-count">{rangeLabel}, по алфавиту</span>
              <Button
                size="sm"
                variant={onlyInSystem ? 'default' : 'outline'}
                onClick={() => patchSearch({ inSystem: onlyInSystem ? undefined : 1, page: undefined })}
                data-testid="filter-in-system"
              >
                Только заведённые
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Клиент</TableHead>
                  {!isMobile && <TableHead>ИНН</TableHead>}
                  {!isMobile && <TableHead>Ответственный</TableHead>}
                  {!isMobile && <TableHead>Отдел</TableHead>}
                  <TableHead>Состояние</TableHead>
                  <TableHead>Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((client) => (
                  <TableRow
                    key={client.external_id}
                    className="cursor-pointer"
                    onClick={() => openClient(client)}
                    data-testid={`client-row-${client.external_id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{client.title ?? '—'}</div>
                      {/* Путь по отделам в строке: без него два клиента с
                          похожими названиями неразличимы, особенно в поиске. */}
                      <div className="text-xs text-muted-foreground">
                        {[client.region, client.city].filter(Boolean).join(', ') ||
                          client.department_path ||
                          'Без отдела'}
                      </div>
                      {query.trim() && client.department_path && (
                        <div className="text-xs text-muted-foreground">
                          {client.department_path}
                        </div>
                      )}
                    </TableCell>
                    {!isMobile && <TableCell>{client.inn ?? '—'}</TableCell>}
                    {!isMobile && <TableCell>{client.assigned_by_name ?? '—'}</TableCell>}
                    {!isMobile && (
                      <TableCell>
                        {/* Отдел хозяйства, если оно заведено; иначе — отдел
                            ответственного из CRM. Разные вещи: первое — наше
                            решение, второе — догадка по менеджеру. */}
                        {client.cluster_name ?? (
                          <span className="text-muted-foreground">
                            {client.department_path?.split('›').pop()?.trim() ?? '—'}
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <StateCell client={client} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <ClientActions
                        dense
                        idSuffix={client.external_id}
                        state={client.state}
                        fieldsCount={client.fields_count}
                        clusterName={client.cluster_name}
                        busy={busyId === client.external_id}
                        onSetupFarm={() => setSetupClient(client)}
                        onAddFields={() => {
                          // Хозяйства ещё нет — «Добавить поля» ведёт в ту же
                          // модалку: два входа, один путь (шаг 4 флоу).
                          if (client.state === 'farm_ready' || client.state === 'in_system') {
                            void runAction(client, flow.goAddFields);
                          } else {
                            setSetupClient(client);
                          }
                        }}
                        onShowOnMap={() => void runAction(client, flow.goToMap)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && !error && (
              <p className="p-6 text-center text-sm text-muted-foreground">
                {query.trim()
                  ? 'Никого не нашли — попробуйте другое название или ИНН.'
                  : 'В этом отделе клиентов нет.'}
              </p>
            )}
            {pages > 1 && (
              <div
                className="flex items-center justify-between gap-2 border-t px-3 py-2 text-sm text-muted-foreground"
                data-testid="clients-pager"
              >
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => goto(page - 1)}
                  data-testid="page-prev"
                >
                  <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                  Назад
                </Button>
                <span data-testid="page-position">
                  Страница {data?.page ?? page} из {pages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= pages}
                  onClick={() => goto(page + 1)}
                  data-testid="page-next"
                >
                  Дальше
                  <ChevronRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
      {setupClient && (
        <FarmSetupDialog
          open
          onOpenChange={(open) => {
            if (!open) setSetupClient(null);
          }}
          client={setupClient}
          departments={departments}
          submitting={flow.busy}
          error={flow.error}
          onSubmit={(result) => void handleFarmSetup(result)}
        />
      )}
    </AppShell>
  );
}
